import { TriangleAlert } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { ConfirmationDialog } from '../../features/dialog/confirmation-dialog.tsx';
import { DialogStateManager } from '../../features/dialog/dialog-provider.tsx';
import { useLocale } from './use-locale.ts';

type UseDiscardChangesWarningResult = {
    confirmDiscard: (onDiscard: () => void, onKeepEditing?: () => void) => void;
};

export function useDiscardChangesWarning(isDirty: boolean): UseDiscardChangesWarningResult {
    const { t } = useLocale('common');

    useEffect(() => {
        if (!isDirty) {
            return;
        }

        const handler = (event: BeforeUnloadEvent): void => {
            event.preventDefault();
        };

        window.addEventListener('beforeunload', handler);

        return () => {
            window.removeEventListener('beforeunload', handler);
        };
    }, [isDirty]);

    const confirmDiscard = useCallback(
        (onDiscard: () => void, onKeepEditing?: () => void) => {
            if (!isDirty) {
                onDiscard();
                return;
            }

            void DialogStateManager.show(ConfirmationDialog, {
                focus: 'destroy',
                icon: TriangleAlert,
                title: t('discard_changes__title'),
                description: t('discard_changes__description'),
                accept: {
                    text: t('discard_changes__confirm'),
                    action: onDiscard,
                },
                reject: {
                    text: t('form__cancel'),
                    action: onKeepEditing,
                },
            });
        },
        [isDirty, t],
    );

    return { confirmDiscard };
}
